import React, { useEffect, useState } from 'react';
import { api } from '@/lib/db';
import { useToast } from '@/contexts/ToastContext';
import { Card, Button, Input } from '@/components/ui/Components';
import SafeIcon from '@/common/SafeIcon';

const SuperAdminFAQ = () => {
  const { addToast } = useToast();
  const [faqs, setFaqs] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ question: '', answer: '' });
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchFaqs(); }, []);

  const fetchFaqs = async () => {
    const data = await api.getFaq();
    setFaqs(data);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addForm.question.trim() || !addForm.answer.trim()) {
      addToast('Kérdés és válasz megadása kötelező', 'error');
      return;
    }
    setSaving(true);
    try {
      await api.addFaq(addForm);
      addToast('GYIK bejegyzés hozzáadva!', 'success');
      setAddForm({ question: '', answer: '' });
      setShowAdd(false);
      fetchFaqs();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (faq) => {
    setEditId(faq.id);
    setEditForm({ question: faq.question, answer: faq.answer });
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.updateFaq(editId, editForm);
      addToast('Bejegyzés frissítve!', 'success');
      setEditId(null);
      fetchFaqs();
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (faq) => {
    if (!confirm(`Törlöd ezt a kérdést?\n"${faq.question}"`)) return;
    try {
      await api.deleteFaq(faq.id);
      addToast('Bejegyzés törölve', 'success');
      fetchFaqs();
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-black text-gray-900">GYIK kezelés</h1>
          <p className="text-gray-500">Szerkeszd a felhasználóknak megjelenő kérdés-válasz párokat.</p>
        </div>
        <Button onClick={() => setShowAdd(!showAdd)}>
          <SafeIcon name="Plus" className="w-4 h-4 mr-2" /> Új bejegyzés
        </Button>
      </div>

      {/* ─── Új bejegyzés form ────────────────────────────────────────── */}
      {showAdd && (
        <Card className="p-6 border-2 border-blue-500">
          <h3 className="font-bold text-gray-900 mb-4">Új kérdés–válasz</h3>
          <form onSubmit={handleAdd} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Kérdés *</label>
              <input
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={addForm.question}
                onChange={e => setAddForm({ ...addForm, question: e.target.value })}
                placeholder="Pl.: Hogyan működik az értékelési folyamat?"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Válasz *</label>
              <textarea
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                rows={4}
                value={addForm.answer}
                onChange={e => setAddForm({ ...addForm, answer: e.target.value })}
                placeholder="Írj részletes választ..."
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Mégse</Button>
              <Button type="submit" disabled={saving}>{saving ? 'Mentés...' : 'Hozzáadás'}</Button>
            </div>
          </form>
        </Card>
      )}

      {/* ─── Lista ────────────────────────────────────────────────────── */}
      <div className="space-y-3">
        {faqs.length === 0 && (
          <Card className="p-10 text-center text-gray-400">
            <SafeIcon name="HelpCircle" className="w-10 h-10 mx-auto mb-3 text-gray-300" />
            <p className="text-sm">Még nincsenek GYIK bejegyzések.</p>
          </Card>
        )}

        {faqs.map((faq, idx) => (
          <Card key={faq.id} className="overflow-hidden">
            {editId === faq.id ? (
              /* ─── Szerkesztő mód ─── */
              <form onSubmit={handleEdit} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kérdés</label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={editForm.question}
                    onChange={e => setEditForm({ ...editForm, question: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Válasz</label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    rows={4}
                    value={editForm.answer}
                    onChange={e => setEditForm({ ...editForm, answer: e.target.value })}
                    required
                  />
                </div>
                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setEditId(null)}>Mégse</Button>
                  <Button type="submit" disabled={saving}>{saving ? 'Mentés...' : 'Mentés'}</Button>
                </div>
              </form>
            ) : (
              /* ─── Olvasó mód ─── */
              <div className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <span className="w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">{faq.question}</p>
                      <p className="text-sm text-gray-500 mt-1 leading-relaxed">{faq.answer}</p>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => openEdit(faq)}
                      className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Szerkesztés"
                    >
                      <SafeIcon name="Edit2" className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Törlés"
                    >
                      <SafeIcon name="Trash2" className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

export default SuperAdminFAQ;
